// src/components/TaskVantage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

/** Small utility used by the Dashboard section */
const cx = (...cls) => cls.filter(Boolean).join(" ");

export default function TaskVantage() {
  const { isAuthenticated } = useAuth0();
  return isAuthenticated ? <TVAppShell /> : <TVLanding />;
}

/* --------------------------- Shared: Brand logo --------------------------- */
const Logo = () => (
  <div className="flex items-center gap-3">
    <img
      src="https://mfc.fit/taskvantagecheck.png"
      alt="Task Vantage"
      className="h-8 w-8 rounded-lg shadow-sm"
      loading="eager"
    />
    <span className="font-semibold text-gray-900">Task Vantage</span>
  </div>
);

/* --------------------------- Landing (logged out) -------------------------- */

/* --------------------------- Landing (logged out) -------------------------- */

function TVLanding() {
  const { loginWithRedirect } = useAuth0();

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      {/* Top bar (sticky + blur) */}
      <header className="sticky top-0 z-40 border-b border-neutral-200 bg-white/80 backdrop-blur">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-6">
          <Logo />

          <div className="hidden items-center gap-3 md:flex">
            <button
              onClick={() => loginWithRedirect({
                authorizationParams: {
                 'ext-branding': 'taskvantage',
                },
                })}
              className="text-sm text-neutral-700 hover:text-neutral-900"
            >
              Login
            </button>
            <button
              onClick={() => loginWithRedirect()}
              className="inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium shadow-sm
                         bg-gradient-to-r from-[#FFAC2F]/70 to-[#FF3FED]/70 text-white
                         hover:from-[#FFAC2F]/80 hover:to-[#FF3FED]/80"
            >
              Get Started
            </button>
          </div>

          <button
            className="md:hidden inline-flex items-center justify-center rounded-xl border border-neutral-200 p-2.5 text-neutral-700"
            onClick={() => loginWithRedirect()}
            aria-label="Menu"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative">
     
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(120%_120%_at_50%_-20%,rgba(255,172,47,0.25),rgba(255,63,237,0.20))] opacity-60 blur-3xl" />

        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 pb-12 pt-12 md:grid-cols-12 md:gap-8 md:px-6">
          {/* Left: text */}
          <div className="md:col-span-6 lg:col-span-7">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium
                            border border-[#FFD7F6] bg-[#FFF3FA] text-[#FF3FED]">
              AI powered workflow automation
            </div>

            <h1 className="text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
              Automate work across <span className="text-[#FF3FED]">every team</span>
            </h1>

            <p className="mt-4 max-w-xl text-neutral-600 md:text-lg">
              Turn playbooks into automated workflows. Standardize intake, route work, and close loops fast.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                onClick={() => loginWithRedirect()}
                className="inline-flex items-center rounded-xl px-5 py-3 text-sm font-medium shadow-sm
                           bg-gradient-to-r from-[#FFAC2F]/70 to-[#FF3FED]/70 text-white
                           hover:from-[#FFAC2F]/80 hover:to-[#FF3FED]/80"
              >
                Start free trial
              </button>

              <button
                onClick={() => loginWithRedirect()}
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium
                           border border-[#FF3FED]/30 text-[#FF3FED] bg-white hover:bg-[#FF3FED]/5"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Book a demo
              </button>
            </div>

            <ul className="mt-6 grid grid-cols-2 gap-3 text-sm text-neutral-600 sm:grid-cols-3">
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-700">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m5 12 4 4L19 6" />
                  </svg>
                </span>
                No credit card
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-700">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m5 12 4 4L19 6" />
                  </svg>
                </span>
                Cancel anytime
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-700">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m5 12 4 4L19 6" />
                  </svg>
                </span>
                Set up in minutes
              </li>
            </ul>
          </div>

          {/* Right: framed image */}
          <div className="md:col-span-6 lg:col-span-5">
            <div className="relative rounded-3xl border border-neutral-200 bg-white p-2 shadow-2xl">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-neutral-200">
                <img src="https://task-vantage.vercel.app/hero-alt.png" alt="Operations hero" className="h-full w-full object-cover" />
              </div>

              <div className="absolute -right-3 -top-3 z-20 inline-flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-neutral-100">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v4m0 12v4M2 12h4m12 0h4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83" />
                  </svg>
                </span>
                <span className="font-medium">Millions of tasks automated</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      
      <section className="mx-auto max-w-7xl px-6 pb-24">
        <h2 className="text-center text-3xl font-semibold text-gray-900">How it works</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <Step n="1" title="Capture" desc="Collect requests from forms, email, and Slack." />
          <Step n="2" title="Orchestrate" desc="Route by rules, skills, and priority." />
          <Step n="3" title="Measure" desc="Track SLAs and remove bottlenecks." />
        </div>
      </section>
    </div>
  );
}


function Step({ n, title, desc }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-pink-100 text-pink-600 text-sm font-semibold">
        {n}
      </span>
      <h3 className="mt-3 font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-gray-600">{desc}</p>
    </div>
  );
}

/* ---------------------------- App shell (logged in) ---------------------------- */

function TVAppShell() {
  const { user, logout } = useAuth0();
  const [active, setActive] = useState("Home");

  const tabs = [
    { key: "Home", label: "Home" },
    { key: "Members", label: "Members" },
    { key: "SSO", label: "SSO" },
    { key: "Security", label: "Security Policies" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-gray-600">
              {user?.name || user?.email}
            </span>
            <img
              alt="avatar"
              src={user?.picture || "https://www.gravatar.com/avatar?d=mp"}
              className="h-8 w-8 rounded-full"
            />
            <button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 py-8 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="md:sticky md:top-16 h-fit">
          <nav className="rounded-xl border bg-white p-2">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={`w-full text-left px-3 py-2 rounded-lg font-medium ${
                  active === t.key
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main panel */}
        <main className="space-y-8">
          {active === "Home" && <TaskVantageDashboard />}
          {active === "Members" && <MembersTab />}
          {active === "SSO" && <SSOConnect />}
          {active === "Security" && <SecurityPolicies />}
        </main>
      </div>
    </div>
  );
}

/* ------------------------------- Dashboard -------------------------------- */

function TaskVantageDashboard() {
  const tone = {
    bg50: "bg-indigo-50",
    text600: "text-indigo-600",
    bg600: "bg-indigo-600",
    hoverBg500: "hover:bg-indigo-500",
    border200: "border-indigo-200",
  };

  const kpis = [
    { label: "Patient discharge efficiency", value: "92%" },
    { label: "Pending lab results", value: "18" },
    { label: "Avg patient stay", value: "2.4d" },
    { label: "Missed critical windows", value: "3" },
  ];

  const workflows = [
    { name: "Patient admissions", runs: 124, success: 0.99, duration: "6m 12s", status: "Healthy" },
    { name: "Lab testing", runs: 76, success: 0.92, duration: "6m 02s", status: "Attention" },
    { name: "Medication orders", runs: 91, success: 0.95, duration: "5m 45s", status: "Healthy" },
    { name: "Surgical prep", runs: 54, success: 0.88, duration: "8m 11s", status: "Degraded" },
  ];

  const myTasks = [
    { id: "TV-2042", title: "Approve MRI scan order", who: "Lisa IP  P", due: "Today", type: "Approval" },
    { id: "TV-2041", title: "Review failed lab test processing", who: "From System", due: "2h", type: "Retry" },
    { id: "TV-2039", title: "Update treatment protocol", who: "Maya R", due: "Tomorrow", type: "Change" },
  ];

  return (
    <div className="space-y-6">
      {/* Filters row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <span className="hidden items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-1.5 sm:flex">
            <svg className="h-4 w-4 text-neutral-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5h18M6 12h12M10 19h4"/></svg>
            <span>All projects</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-1.5">
            <svg className="h-4 w-4 text-neutral-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
            <span>Last 7 days</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-1.5">
            <svg className="h-4 w-4 text-neutral-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 7a4 4 0 1 1-8 0"/><path d="M12 3v4"/></svg>
            <span>Production</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button className="inline-flex items-center rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M7 12h10M10 18h4"/></svg>
            View options
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-neutral-500">{k.label}</div>
            <div className="mt-1 flex items-baseline justify-between">
              <div className="text-2xl font-semibold">{k.value}</div>
            </div>
            {/* tiny bars */}
            <div className="mt-3 grid h-10 grid-cols-10 gap-1">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="flex items-end">
                  <div
                    className={cx("w-full rounded-md", i % 2 === 0 ? "bg-neutral-200" : tone.bg600)}
                    style={{ height: `${30 + ((i * 13) % 60)}%` }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Two column zone */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left: My tasks */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">My tasks</h3>
              <a className={cx("text-xs", tone.text600)} href="#">View all</a>
            </div>
            <ul className="mt-3 divide-y divide-neutral-100">
              {myTasks.map((t) => (
                <li key={t.id} className="flex items-start justify-between gap-3 py-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className={cx("mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-xl", tone.bg50)}>
                      <svg className={cx("h-4 w-4", tone.text600)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H7l-4-4V5a2 2 0 0 1 2-2h11"/>
                      </svg>
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{t.title}</div>
                      <div className="mt-0.5 text-xs text-neutral-500">{t.who}</div>
                    </div>
                  </div>
                  <div className="shrink-0 text-xs text-neutral-500">{t.due}</div>
                </li>
              ))}
            </ul>
            <div className="mt-3">
              <button className={cx("inline-flex w-full items-center justify-center rounded-xl px-3 py-2 text-sm font-medium text-white", tone.bg600, tone.hoverBg500)}>
                Open patient queue
              </button>
            </div>
          </div>
        </div>

        {/* Right: Flow health + Activity */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Flow health</h3>
              <a className={cx("text-xs", tone.text600)} href="#">See details</a>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { name: "ER admissions", runs: 124, success: 90, duration: "6m 12s", status: "Healthy" },
                { name: "Surgical prep", runs: 76, success: 92, duration: "8m 92s", status: "Attention" },
                { name: "Pharmacy fulfillment", runs: 91, success: 96, duration: "7m 31s", status: "Healthy" },
              ].map((w) => (
                <div key={w.name} className="rounded-xl border border-neutral-200 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 truncate text-sm font-medium">{w.name}</div>
                    <StatusBadge status={w.status} />
                  </div>
                  <div className="mt-2 grid grid-cols-3 text-center text-xs text-neutral-600">
                    <div>
                      <div className="font-semibold text-neutral-900">{w.runs}</div>
                      <div>Runs</div>
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900">{w.success}%</div>
                      <div>Success</div>
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900">{w.duration}</div>
                      <div>Avg Time</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Run activity (vertical bars) */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
  <div className="flex items-center justify-between">
    <h3 className="text-sm font-semibold">Run activity</h3>
    <span className="text-xs text-neutral-500">Last 24 hours</span>
  </div>

  {/* 24 vertical columns + bottom-aligned bars */}
  <div
    className="mt-4 grid gap-1"
    style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}
  >
    {Array.from({ length: 24 }).map((_, i) => (
      <div
        key={i}
        className="h-24 w-full rounded-md bg-neutral-100 flex items-end"
      >
        <div
          className={cx(
            "w-full rounded-md",
            i % 6 === 0 ? "bg-neutral-300" : "bg-indigo-600"
          )}
          style={{ height: `${30 + Math.abs(Math.sin(i)) * 60}%` }}
        />
      </div>
    ))}
  </div>
</div>

        </div>
      </section>

      {/* Table: Top workflows */}
      <section>
        <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Top workflows</h3>
            <div className="flex items-center gap-2">
              <input className="w-48 rounded-xl border border-neutral-200 bg-white px-3 py-1.5 text-sm outline-none placeholder:text-neutral-400" placeholder="Filter by name" />
              <button className="inline-flex items-center rounded-xl border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50">
                Export
              </button>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-xs text-neutral-500">
                  <th className="py-2 pr-4">Workflow</th>
                  <th className="py-2 pr-4">Runs today</th>
                  <th className="py-2 pr-4">Success rate</th>
                  <th className="py-2 pr-4">Avg duration</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-0 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {workflows.map((w) => (
                  <tr key={w.name} className="border-b border-neutral-100 last:border-b-0">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <span className={cx("inline-flex h-8 w-8 items-center justify-center rounded-xl", tone.bg50)}>
                          <svg className={cx("h-4 w-4", tone.text600)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="14" rx="2"/><path d="M7 21h10"/>
                          </svg>
                        </span>
                        <span className="font-medium">{w.name}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">{w.runs}</td>
                    <td className="py-3 pr-4">{Math.round(w.success * 100)}%</td>
                    <td className="py-3 pr-4">{w.duration}</td>
                    <td className="py-3 pr-4"><StatusBadge status={w.status} /></td>
                    <td className="py-3 pr-0 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button className="rounded-lg border border-neutral-200 bg-white px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-50">Open</button>
                        <button className={cx("rounded-lg px-2 py-1 text-xs text-white", tone.bg600, tone.hoverBg500)}>Run</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Healthy: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
    Attention: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
    Degraded: { bg: "bg-rose-50", text: "text-rose-700", dot: "bg-rose-500" },
  };
  const s = map[status] || map.Healthy;
  return (
    <span className={cx("inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium", s.bg, s.text)}>
      <span className={cx("h-1.5 w-1.5 rounded-full", s.dot)} />
      {status}
    </span>
  );
}

/* -------------------------------- Members tab -------------------------------- */

function MembersTab() {
  const initialGroups = useMemo(
    () => [
      {
        groupLabel: "Group",
        members: [
          { id: "john@atko.email", name: "john@atko.email", email: "john@atko.email", role: "Admin" },
          { id: "stanleyvarner@atko.email", name: "stanleyvarner@atko.email", email: "stanleyvarner@atko.email", role: "Admin" },
        ],
      },
      {
        groupLabel: "Group",
        members: [
          { id: "steve@atko.email", name: "steve@atko.email", email: "steve@atko.email", role: "Executive" },
          { id: "jon@atko.email", name: "jon@atko.email", email: "jon@atko.email", role: "Executive" },
        ],
      },
      {
        groupLabel: "Group",
        members: [
          { id: "amie@atko.email", name: "amie@atko.email", email: "amie@atko.email", role: "Product" },
          { id: "jennifer@atko.email", name: "jennifer@atko.email", email: "jennifer@atko.email", role: "Product" },
        ],
      },
    ],
    []
  );

  const [groups, setGroups] = useState(initialGroups);
  const [pending, setPending] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Member");

  const roles = ["Admin", "Executive", "Product", "Member"];

  const updateRole = (memberId, newRole) => {
    setGroups((prev) =>
      prev.map((g) => ({
        ...g,
        members: g.members.map((m) => (m.id === memberId ? { ...m, role: newRole } : m)),
      }))
    );
  };

  const sendInvite = () => {
    if (!inviteEmail.trim()) return;
    setPending((p) => [...p, { email: inviteEmail.trim(), role: inviteRole, invitedBy: "You" }]);
    setInviteEmail("");
    setInviteRole("Member");
  };

  return (
    <div className="space-y-8">
      {/* Members card */}
      <section className="rounded-2xl border bg-white shadow-sm">
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">Members</h2>
          <p className="text-sm text-gray-600">Manage the members of the organization.</p>
        </div>

        <div className="p-0">
          {/* Current Members & Groups header */}
          <div className="px-6 py-4">
            <h3 className="text-sm font-semibold text-gray-900">Current Members &amp; Groups</h3>
            <p className="text-sm text-gray-600">The current organization members.</p>
          </div>

          {/* Groups */}
          {groups.map((g, gi) => (
            <div key={gi} className="border-t">
              <div className="px-6 py-3 text-sm font-medium text-gray-600">{g.groupLabel}</div>
              {g.members.map((m) => (
                <div key={m.id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <Avatar email={m.email} />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{m.name}</div>
                      <div className="text-xs text-gray-500">{m.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={m.role}
                      onChange={(e) => updateRole(m.id, e.target.value)}
                      className="rounded-md border-gray-300 text-sm"
                    >
                      {roles.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                    <button className="h-8 w-8 grid place-items-center rounded-md border text-gray-500 hover:bg-gray-50">
                      ⋮
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Pending Invitations */}
          <div className="border-t px-6 py-4">
            <h3 className="text-sm font-semibold text-gray-900">Pending Invitations</h3>
            <p className="text-sm text-gray-600">
              Invitations that have been sent out but have not yet been redeemed.
            </p>

            <div className="mt-3 overflow-hidden rounded-lg border">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Email</th>
                    <th className="px-4 py-2 text-left font-medium">Role</th>
                    <th className="px-4 py-2 text-left font-medium">Invited By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {pending.length === 0 ? (
                    <tr>
                      <td className="px-4 py-3 text-gray-500" colSpan={3}>
                        No pending invitations.
                      </td>
                    </tr>
                  ) : (
                    pending.map((p, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3">{p.email}</td>
                        <td className="px-4 py-3">{p.role}</td>
                        <td className="px-4 py-3">{p.invitedBy}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Invite form */}
          <div className="border-t px-6 py-5">
            <h3 className="text-sm font-semibold text-gray-900">Invite team members</h3>
            <p className="text-sm text-gray-600">
              Invite team members to join this organization using their email address.
            </p>

            <div className="mt-3 grid gap-3 md:grid-cols-[1fr_220px_auto]">
              <input
                type="email"
                placeholder="name@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="rounded-md border-gray-300"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="rounded-md border-gray-300"
              >
                {["Member", "Product", "Executive", "Admin"].map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <button
                onClick={sendInvite}
                className="rounded-md bg-gray-900 px-4 py-2 text-white font-medium hover:bg-black"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Avatar({ email }) {
  const initials = email ? email.split("@")[0].slice(0, 2).toUpperCase() : "MM";
  return (
    <div className="h-9 w-9 rounded-full bg-gray-200 grid place-items-center text-gray-700 text-sm font-semibold">
      {initials}
    </div>
  );
}

function SSOConnect() {
  return (
    <section className="rounded-2xl border bg-white shadow-sm">
      <div className="mx-auto max-w-3xl px-8 py-16 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">
          Connect Your IDP To Begin
        </h2>

        <p className="mt-4 text-gray-600">
          Connect to your IDP to sync your users.
        </p>

        <a
          href="https://custom.authdemolab.com/self-service/connections-flow?ticket=xClesYnS6SgN5v4Eta9Ac1njJAtQzaEz"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center rounded-xl bg-gray-900 px-5 py-3 text-white font-medium hover:bg-black"
        >
          Create Your SSO Connection
          <span className="ml-2">→</span>
        </a>

        <p className="mt-4 text-sm text-gray-500">
          (You must be logged in with an <span className="font-medium">admin</span> role in your organization.)
        </p>
      </div>
    </section>
  );
}


/* --------------------------- Security Policies tab -------------------------- */

function SecurityPolicies() {
  const [enforceMfa, setEnforceMfa] = useState(false);
  const [exemptDomains, setExemptDomains] = useState("");
  const [allowOtp, setAllowOtp] = useState(true);
  const [allowSecurityKeys, setAllowSecurityKeys] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    }, 700);
  };

  return (
    <section className="rounded-2xl border bg-white shadow-sm">
      {/* Header */}
      <div className="px-6 pt-6">
        <h2 className="text-2xl font-semibold text-gray-900">Security Policies</h2>
        <p className="mt-1 text-gray-600">
          Manage the security policies of your organization.
        </p>
      </div>

      {/* Body */}
      <div className="px-6 pb-6">
        {/* Card */}
        <div className="mt-6 rounded-xl border border-gray-200">
          {/* Section header */}
          <div className="px-5 py-4">
            <h3 className="text-sm font-semibold text-gray-900">
              Multi-Factor Authentication (MFA)
            </h3>
            <p className="text-sm text-gray-600">
              Configure the MFA policies for your organization.
            </p>
          </div>

          <div className="h-px bg-gray-100" />

          {/* Enforce MFA toggle */}
          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-900">
                Enforce Multi-Factor Authentication
              </div>
              <div className="text-sm text-gray-600">
                Users will be required to verify their identity with a second factor.
              </div>
            </div>

            {/* switch */}
            <button
              type="button"
              onClick={() => setEnforceMfa((v) => !v)}
              aria-pressed={enforceMfa}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                enforceMfa ? "bg-indigo-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                  enforceMfa ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <div className="h-px bg-gray-100" />

          {/* Exempt domains */}
          <div className="px-5 py-4">
            <label className="block text-sm font-medium text-gray-900">
              Do not enforce MFA for the following e-mail domains
            </label>
            <input
              value={exemptDomains}
              onChange={(e) => setExemptDomains(e.target.value)}
              placeholder="example.com, auth0.com"
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-2 text-sm text-gray-600">
              Enter a comma-separated list of e-mail domains.
            </p>
          </div>

          <div className="h-px bg-gray-100" />

          {/* Providers */}
          <div className="px-5 py-4">
            <div className="text-sm font-medium text-gray-900">
              Select which MFA providers your users are allowed to use
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {/* OTP card */}
              <button
                type="button"
                onClick={() => setAllowOtp((v) => !v)}
                className={`flex items-center justify-between rounded-xl border p-4 text-left ${
                  allowOtp ? "border-gray-900" : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="inline-grid h-9 w-9 place-items-center rounded-xl bg-gray-100">
                    <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 7a5 5 0 1 1-4 8l-2 2h-2v-2l2-2a5 5 0 0 1 6-6Z" />
                    </svg>
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">One-time Password</div>
                    <div className="text-sm text-gray-600">OTP using Google Authenticator or similar.</div>
                  </div>
                </div>
                <input type="checkbox" readOnly checked={allowOtp} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
              </button>

              {/* Security Keys card */}
              <button
                type="button"
                onClick={() => setAllowSecurityKeys((v) => !v)}
                className={`flex items-center justify-between rounded-xl border p-4 text-left ${
                  allowSecurityKeys ? "border-gray-900" : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="inline-grid h-9 w-9 place-items-center rounded-xl bg-gray-100">
                    <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="10" width="18" height="8" rx="2" />
                      <path d="M9 6h6v4" />
                    </svg>
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Security Keys</div>
                    <div className="text-sm text-gray-600">WebAuthn-compliant security keys (like FIDO2).</div>
                  </div>
                </div>
                <input type="checkbox" readOnly checked={allowSecurityKeys} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="mt-4 flex items-center justify-end gap-3">
          {saved && <span className="text-sm text-emerald-600">Saved ✓</span>}
          <button
            onClick={save}
            disabled={saving}
            className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </section>
  );
}
